import { Table } from '@dixa/lib-shared-habla';
import { type Cell, flexRender, type Row } from '@tanstack/react-table';
import classnames from 'classnames';
import type { SearchConversationEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { MouseEventHandler, type ReactNode, useCallback } from 'react';

import { AppUrlOptions, createAppUrl } from 'utils/url/createAppUrl';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import type { ConversationsTableFeatures } from '../../../conversationsTable.columns';
import tableStyles from '../../../conversationsTable.module.scss';
import {
  getConversationsTableColumnId,
  getIsConversationsTableScrollable,
} from '../../../conversationsTable.utils';
import { ConversationsTableFillerCell } from '../../fillerCell/conversationsTableFillerCell';

import styles from './conversationsTableConversationRow.module.scss';
import { ConversationRowContext } from './utils/conversationRowContext';

export type ConversationsTableConversationRowProps = {
  /** The table row of the conversation to render. */
  row: Row<ConversationsTableFeatures, SearchConversationEnriched>;
  /** Whether to show the filter menu item in the row menu. Defaults to true. */
  showRowMenuFilter?: boolean;
  /** When provided, replaces the default navigation on row click. */
  onRowClick?: (conversation: SearchConversationEnriched) => void;
  /** When provided, renders the row's action in place of the button that previews the conversation. */
  renderAction?: (conversation: SearchConversationEnriched) => ReactNode;
  /** Whether the row should be visually highlighted. */
  isHighlighted?: boolean;
};

export const ConversationsTableConversationRow = ({
  row,
  showRowMenuFilter = true,
  onRowClick,
  renderAction,
  isHighlighted,
}: ConversationsTableConversationRowProps): JSX.Element => {
  const conversation = row.original;
  const navigate = useAppNavigate();

  const navigateToConversation = useCallback<MouseEventHandler<HTMLElement>>(
    (e) => {
      const isCtrlOrCmdPressed = e.metaKey || e.ctrlKey;

      const urlOptions: AppUrlOptions<'/conversation/:csid'> = {
        path: '/conversation/:csid',
        params: {
          path: {
            csid: conversation.id.toString(),
          },
        },
      };

      if (isCtrlOrCmdPressed) {
        window.open(createAppUrl(urlOptions), '_blank');
      } else {
        navigate(urlOptions);
      }
    },
    [conversation.id, navigate],
  );

  /** Handles clicking the row. */
  const handleRowClick = useCallback<MouseEventHandler<HTMLElement>>(
    (e) => {
      const { target } = e;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLButtonElement ||
        target instanceof SVGElement ||
        // Assumes that the first cell in the row is the implicitly added
        // checkbox cell. If for some reason the checkbox is ever going to be
        // optional then it should be guarded here.
        (target instanceof HTMLTableCellElement &&
          target.parentElement?.childNodes[0] === target) ||
        (target instanceof HTMLTableCellElement &&
          target.parentElement?.childNodes[
            target.parentElement?.childNodes.length - 1
          ] === target)
      ) {
        return;
      }

      if (onRowClick) {
        onRowClick(conversation);
        return;
      }

      navigateToConversation(e);

      ampli.conversationsGoToConversationClicked();
    },
    [conversation, navigateToConversation, onRowClick],
  );

  const renderCell = (
    cell: Cell<ConversationsTableFeatures, SearchConversationEnriched, unknown>,
  ) => (
    <Table.Td
      key={cell.column.id}
      columnId={getConversationsTableColumnId(cell.column)}
      className={classnames({
        [tableStyles.tdUntruncated]: !(
          cell.column.columnDef.meta?.truncate ?? true
        ),
      })}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </Table.Td>
  );

  return (
    <ConversationRowContext.Provider
      value={{
        conversation,
        showRowMenuFilter,
        renderAction,
        density: row.table.options.meta?.layout?.density,
      }}
    >
      <Table.Tr
        className={classnames(styles.root, {
          [styles.rootHighlighted]: isHighlighted,
        })}
        aria-current={isHighlighted || undefined}
        rowId={conversation.id}
        onClick={handleRowClick}
      >
        {getIsConversationsTableScrollable(row.table) ? (
          <>
            {row.getStartVisibleCells().map(renderCell)}
            {row.getCenterVisibleCells().map(renderCell)}
            <ConversationsTableFillerCell />
            {row.getEndVisibleCells().map(renderCell)}
          </>
        ) : (
          row.getVisibleCells().map(renderCell)
        )}
      </Table.Tr>
    </ConversationRowContext.Provider>
  );
};
